import type { SvgIconComponent } from "@mui/icons-material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, ClickAwayListener, IconButton, Popper, Typography } from "@mui/material";
import { forwardRef, useRef, useState } from "react";
import { CAMPAIGN_ACTION_OUT_OF_SCOPE_TOOLTIP } from "@/constants/campaignLifecycle";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { usePermissions } from "@/hooks/usePermissions";
import type { CampaignAllowedActions } from "@/interfaces/campaing";
import type { ContactsMenuMode } from "@/interfaces/giftLinks";
import ExportDropdown from "./ExportDropdown";
import MoreOptionsDropdown from "./MoreOptionsDropdown";

const BORDERED_BTN_SX = {
  border: "1px solid var(--color-smoke)",
  borderRadius: "var(--radius-btn)",
  width: 40,
  height: 40,
  color: "var(--color-graphite)",
} as const;

const MOBILE_BTN_SIZE = 55;

interface BorderedIconButtonProps {
  icon: SvgIconComponent;
  onClick?: () => void;
  "aria-label": string;
  "aria-expanded"?: boolean;
  active?: boolean;
  mobile?: boolean;
  disabled?: boolean;
  /** Hover text; carried by a wrapper because a disabled button receives no pointer events. */
  title?: string;
}

const BorderedIconButton = forwardRef<HTMLButtonElement, BorderedIconButtonProps>(
  function BorderedIconButton(
    {
      icon: Icon,
      onClick,
      "aria-label": label,
      "aria-expanded": ariaExpanded,
      active = false,
      mobile = false,
      disabled = false,
      title,
    },
    ref,
  ) {
    const button = (
      <IconButton
        ref={ref}
        size="small"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        aria-expanded={ariaExpanded}
        sx={{
          ...BORDERED_BTN_SX,
          width: mobile ? MOBILE_BTN_SIZE : 40,
          height: mobile ? MOBILE_BTN_SIZE : 40,
          ...(active && { bgcolor: "var(--color-light-dust)", color: "var(--color-dark-dust)" }),
          "&:hover": { bgcolor: "var(--color-light-dust)" },
        }}
      >
        <Icon sx={{ fontSize: 25 }} />
      </IconButton>
    );
    return title ? (
      <Box component="span" title={title} sx={{ display: "inline-flex", flexShrink: 0 }}>
        {button}
      </Box>
    ) : (
      button
    );
  },
);

export interface CampaignActionButtonsProps {
  alertCount?: number;
  analyticsActive?: boolean;
  onWarning?: () => void;
  onNotifications?: () => void;
  onAnalytics?: () => void;
  onExportCsv?: () => void;
  onRedemptionReport?: () => void;
  onImpactReport?: () => void;
  onThankYouMessages?: () => void;
  onRename?: () => void;
  onClone?: () => void;
  onEdit?: () => void;
  onPreview?: () => void;
  onManageContacts?: () => void;
  onDelete?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onCancel?: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
  // Forwarded: Universal Link only — turn the shared /u/ link off/on, and copy it.
  onDisable?: () => void;
  onEnable?: () => void;
  onCopyLink?: () => void;
  /** Forwarded: open the Redeem link & QR modal (gfg + Active only; the card decides whether to pass it). */
  onRedeemQr?: () => void;
  /** Forwarded to MoreOptionsDropdown: does the "Preview" item render at all? A view-only role
   *  with no demo link to open has nothing to preview, so it is hidden rather than dead. */
  canPreview?: boolean;
  canPause?: boolean;
  canResume?: boolean;
  canDisable?: boolean;
  canEnable?: boolean;
  canCancel?: boolean;
  canArchive?: boolean;
  canRestore?: boolean;
  canDelete?: boolean;
  /** Forwarded: the greyed "why no terminal action" note (null when one is available). */
  blockedReason?: string | null;
  /** Forwarded to MoreOptionsDropdown: does the "Edit" item render at all? */
  canEdit?: boolean;
  /** Forwarded to MoreOptionsDropdown: which surface the contacts item opens (null: none). */
  contactsMode?: ContactsMenuMode | null;
  /** Forwarded: present only for an automated campaign, which is the only kind with its own card. */
  onChangeCard?: () => void;
  /** Forwarded: that card has been declining, so the item reads as a fix rather than a setting. */
  cardFailing?: boolean;
  /** Which actions this campaign allows the caller (API-computed); also forwarded to the menu. */
  allowedActions: CampaignAllowedActions;
}

export default function CampaignActionButtons({
  alertCount = 0,
  analyticsActive = false,
  onWarning,
  onNotifications,
  onAnalytics,
  onExportCsv,
  onRedemptionReport,
  onImpactReport,
  onThankYouMessages,
  onRename,
  onClone,
  onEdit,
  onPreview,
  onManageContacts,
  onDelete,
  onPause,
  onResume,
  onCancel,
  onArchive,
  onRestore,
  onDisable,
  onEnable,
  onCopyLink,
  onRedeemQr,
  onChangeCard,
  cardFailing,
  canPreview = true,
  canPause = false,
  canResume = false,
  canDisable = false,
  canEnable = false,
  canCancel = false,
  canArchive = false,
  canRestore = false,
  canDelete = false,
  blockedReason = null,
  canEdit = true,
  contactsMode = "contacts",
  allowedActions,
}: CampaignActionButtonsProps) {
  const [exportOpen, setExportOpen] = useState(false);
  const exportButtonRef = useRef<HTMLButtonElement>(null);

  const [moreOpen, setMoreOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  const isMobile = useIsMobile();
  const btnSize = isMobile ? MOBILE_BTN_SIZE : 40;

  // Notifications open for everyone, read-only where the caller may not edit THIS campaign. The
  // bounce fixer is a write surface: hidden from view-only roles, greyed outside the caller's groups.
  const { can } = usePermissions();
  const canEditCampaign = can("edit", "CAMPAIGN");
  const editTitle = allowedActions.edit ? undefined : CAMPAIGN_ACTION_OUT_OF_SCOPE_TOOLTIP;

  return (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
      {canEditCampaign && alertCount > 0 && (
        <Box sx={{ position: "relative", flexShrink: 0 }} title={editTitle}>
          <IconButton
            size="small"
            onClick={onWarning}
            disabled={!allowedActions.edit}
            aria-label={`${alertCount} alert${alertCount !== 1 ? "s" : ""}`}
            sx={{
              bgcolor: "var(--color-good-wine)",
              color: "white",
              borderRadius: "var(--radius-btn)",
              width: btnSize,
              height: btnSize,
              "&:hover": { bgcolor: "var(--color-good-wine-2)" },
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 25 }} />
          </IconButton>
          <Box
            sx={{
              position: "absolute",
              top: -4,
              right: -4,
              bgcolor: "var(--color-good-wine-bg)",
              border: "1px solid var(--color-good-wine)",
              borderRadius: "100px",
              minWidth: 16,
              height: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 0.5,
            }}
          >
            <Typography
              sx={{ fontSize: 9, fontWeight: 700, color: "var(--color-good-wine)", lineHeight: 1 }}
            >
              {alertCount}
            </Typography>
          </Box>
        </Box>
      )}

      <BorderedIconButton
        icon={NotificationsOutlinedIcon}
        onClick={onNotifications}
        aria-label="Notifications"
        mobile={isMobile}
      />
      <BorderedIconButton
        icon={InsertChartOutlinedIcon}
        onClick={onAnalytics}
        aria-label="Analytics"
        active={analyticsActive}
        mobile={isMobile}
      />

      {/* Export / Download button with dropdown */}
      <ClickAwayListener onClickAway={() => setExportOpen(false)}>
        <Box sx={{ flexShrink: 0 }}>
          <BorderedIconButton
            ref={exportButtonRef}
            icon={FileDownloadOutlinedIcon}
            onClick={() => setExportOpen((prev) => !prev)}
            aria-label="Export"
            aria-expanded={exportOpen}
            active={exportOpen}
            mobile={isMobile}
          />
          <Popper
            open={exportOpen}
            anchorEl={exportButtonRef.current}
            placement="bottom-end"
            sx={{ zIndex: 1300 }}
          >
            <ExportDropdown
              onClose={() => setExportOpen(false)}
              onExportCsv={onExportCsv}
              onRedemptionReport={onRedemptionReport}
              onImpactReport={onImpactReport}
              onThankYouMessages={onThankYouMessages}
            />
          </Popper>
        </Box>
      </ClickAwayListener>

      {/* More options button with dropdown */}
      {(canEditCampaign || canPreview || canEdit || contactsMode) && (
        <ClickAwayListener onClickAway={() => setMoreOpen(false)}>
          <Box sx={{ flexShrink: 0 }}>
            <BorderedIconButton
              ref={moreButtonRef}
              icon={MoreHorizIcon}
              onClick={() => setMoreOpen((prev) => !prev)}
              aria-label="More options"
              aria-expanded={moreOpen}
              active={moreOpen}
              mobile={isMobile}
            />
            <Popper
              open={moreOpen}
              anchorEl={moreButtonRef.current}
              placement="bottom-end"
              sx={{ zIndex: 1300 }}
            >
              <MoreOptionsDropdown
                onClose={() => setMoreOpen(false)}
                onRename={onRename}
                onClone={onClone}
                onEdit={onEdit}
                onPreview={onPreview}
                onManageContacts={onManageContacts}
                onChangeCard={onChangeCard}
                cardFailing={cardFailing}
                onDelete={onDelete}
                onPause={onPause}
                onResume={onResume}
                onCancel={onCancel}
                onArchive={onArchive}
                onRestore={onRestore}
                onDisable={onDisable}
                onEnable={onEnable}
                onCopyLink={onCopyLink}
                onRedeemQr={onRedeemQr}
                canPreview={canPreview}
                canPause={canPause}
                canResume={canResume}
                canDisable={canDisable}
                canEnable={canEnable}
                canCancel={canCancel}
                canArchive={canArchive}
                canRestore={canRestore}
                canDelete={canDelete}
                blockedReason={blockedReason}
                canEdit={canEdit}
                contactsMode={contactsMode}
                allowedActions={allowedActions}
              />
            </Popper>
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
}
